import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AgeBandModel, FoodMenu, InclusionExclusion } from '@grpc-clients/external/mercury/v1/attraction/attraction.pb';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { GenericApiErrorComponent } from '@shared/components/error/generic-api-error/generic-api-error.component';
import { ActivitiesSearchService } from '@shared/services/search/activities-search.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { floorStr } from '@shared/utils/floor';
import { AVAILABILITY_FORM_KEY, AVAILABILITY_PREBOOK_DETAILS } from 'app.config';
import { EMPTY, catchError, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import * as fromActions from '../actions/travel-activities-details.actions';
const languageMapper = {
  'zh-cn': 'zh-Hans',
  'zh-hk': 'zh-Hant',
  'zh-tw': 'zh-Hant',
  'zh': 'zh',
};

@Injectable()
export class ActivitiesDetailsEffects {
  constructor(
    private actions$: Actions,
    private siteSettingsService: SiteSettingsService,
    private activitiesSearchService: ActivitiesSearchService,
    private snackBar: MatSnackBar
  ) {}

  public getActivitiesDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_ACTIVITIES_DETAILS),
      switchMap((action: fromActions.GetActivitiesDetails) =>
        this.siteSettingsService.fetchSiteSettings().pipe(
          take(1),
          switchMap((siteSettings: ISiteSettings) =>
            this.activitiesSearchService.details(siteSettings.language, action.id).pipe(
              map((data) => {
                if (data.reviewRating) {
                  const updatedReviewRating = floorStr(data.reviewRating.value, 1);
                  data.reviewRating.value = updatedReviewRating;
                }
                if (data.supportedLanguages.length) {
                  const siteLanguageCode = siteSettings.language.split('-')[0];

                  const supportedLangs = data.supportedLanguages.filter(
                    (language) => !!language?.length && !/xx/.test(language)
                  );
                  data.supportedLanguages = processSupportedLanguages(supportedLangs, siteLanguageCode).sort(sortFn);
                }

                data.ageBands = data.ageBands.filter((ageBand: AgeBandModel) => ageBand.maxTravelersPerBooking);

                data.description.value = processRawText(data.description.value);
                if (data.itinerary?.itineraryDescription) {
                  data.itinerary.itineraryDescription.value = processRawText(data.itinerary.itineraryDescription.value);
                }

                if (data.itinerary?.itineraryActivityInfo?.foodMenus?.length) {
                  data.itinerary.itineraryActivityInfo.foodMenus =
                    data.itinerary.itineraryActivityInfo.foodMenus.sort(itineraryfoodMenuSorter);
                }

                data.inclusions = data.inclusions.filter(
                  (inclusion: InclusionExclusion) => !!inclusion?.description?.value
                );

                return new fromActions.GetActivitiesDetailsSuccess(data);
              }),
              catchError(() => of(new fromActions.GetActivitiesDetailsFailure()))
            )
          )
        )
      )
    )
  );

  public getAvailableRanges$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_ACTIVITIES_AVAILABLE_RANGES),
      switchMap((action: fromActions.GetActivitiesAvailableRanges) =>
        this.activitiesSearchService.availableRanges(action.id).pipe(
          map((ranges) => new fromActions.GetActivitiesAvailableRangesSuccess(ranges)),
          catchError(() => of(new fromActions.GetActivitiesAvailableRangesFailure()))
        )
      )
    )
  );

  public checkAvailability$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CHECK_ACTIVITIES_AVAILABILITY, fromActions.RESTORE_ACTIVITIES_AVAILABILITY_FORM),
      switchMap((action: fromActions.CheckActivitiesAvailability | fromActions.RestoreActivitiesAvailabilityForm) =>
        this.siteSettingsService.fetchSiteSettings().pipe(
          take(1),
          switchMap((siteSettings) =>
            this.activitiesSearchService.checkAvailability(
              siteSettings.language,
              siteSettings.currency,
              action.id,
              action.date,
              action.guests
            )
          ),
          takeUntil(this.actions$.pipe(ofType(fromActions.CANCEL_RETRIEVE_ACTIVITIES_AVAILABILITY))),
          map((options) => new fromActions.CheckActivitiesAvailabilitySuccess(options, action.id)),
          catchError(() => of(new fromActions.CheckActivitiesAvailabilityFailure()))
        )
      )
    )
  );

  public storeAvailabilityForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CHECK_ACTIVITIES_AVAILABILITY),
        tap((action: fromActions.CheckActivitiesAvailability) =>
          localStorage.setItem(
            AVAILABILITY_FORM_KEY,
            JSON.stringify({ date: action.date, guests: action.guests, id: action.id })
          )
        )
      ),
    {
      dispatch: false,
    }
  );

  public storeActivitiesPreBookDetails$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.SELECT_ACTIVITIES_AVAILABILITY_OPTION),
        tap((action: fromActions.SelectActivitiesAvailabilityOption) => {
          localStorage.setItem(
            AVAILABILITY_PREBOOK_DETAILS,
            JSON.stringify({ productId: action.productId, id: action.id })
          );
        })
      ),
    { dispatch: false }
  );

  public restoreAvailabilityForm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_ACTIVITIES_DETAILS_SUCCESS),
      switchMap((action: fromActions.GetActivitiesDetailsSuccess) => {
        const data = JSON.parse(localStorage.getItem(AVAILABILITY_FORM_KEY));

        return data?.id === action.payload.productId
          ? of(new fromActions.RestoreActivitiesAvailabilityForm(data.id, new Date(data.date), data.guests))
          : EMPTY;
      })
    )
  );

  public restoreSelectedAvailabilityOption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CHECK_ACTIVITIES_AVAILABILITY_SUCCESS),
      switchMap((action: fromActions.CheckActivitiesAvailabilitySuccess) => {
        const data = JSON.parse(localStorage.getItem(AVAILABILITY_PREBOOK_DETAILS));

        return data?.productId === action.productId
          ? of(new fromActions.RestoreActivitiesAvailabilitySelected(data.id))
          : EMPTY;
      })
    )
  );

  public clearAvailabilityForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_ACTIVITIES_AVAILABILITY),
        tap(() => {
          localStorage.removeItem(AVAILABILITY_FORM_KEY);
          localStorage.removeItem(AVAILABILITY_PREBOOK_DETAILS);
        })
      ),
    { dispatch: false }
  );

  public error$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          fromActions.CHECK_ACTIVITIES_AVAILABILITY_FAILURE,
          fromActions.GET_ACTIVITIES_DETAILS_FAILURE,
          fromActions.GET_ACTIVITIES_AVAILABLE_RANGES_FAILURE
        ),
        tap(() => {
          this.snackBar.openFromComponent(GenericApiErrorComponent, { duration: 5000 });
        })
      ),
    { dispatch: false }
  );
}

function processSupportedLanguages(supportedLanguages: string[], siteLanguageCode: string): string[] {
  return Array.from(
    supportedLanguages
      .map((lang) => (/zh/.test(lang) ? languageMapper[lang.toLowerCase()] : lang))
      .reduce((langSet, languageCode) => {
        const code = ['zh-Hant', 'zh-Hans'].includes(languageCode) ? languageCode : languageCode.split('-')[0];
        return langSet.add(new Intl.DisplayNames([siteLanguageCode], { type: 'language' }).of(code));
      }, new Set<string>())
  );
}

const sortFn = (a: string, b: string) => a.localeCompare(b);

function processRawText(rawText: string) {
  if (!rawText) {
    return rawText;
  }
  const splitText = rawText.replace('\\n', '').trim().split(/•|\*/);
  const listItems = splitText
    .map((text, index) => {
      const paragraph = text ? `<p>${text.trim()}</p>` : '';
      return index === 0 ? paragraph : text ? `<li>${text.trim()}</li>` : '';
    })
    .map((item, index) => {
      return index === 0 ? item : `<ul class="activities-list">${item}</ul>`;
    })
    .join('');
  return listItems;
}

const itineraryfoodMenuSorter = (a: FoodMenu, b: FoodMenu) => b.course.value.localeCompare(a.course.value);
